import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import Button from 'components/Button';
import Chip from 'components/Chip';
import DeleteConfirmModal from 'components/DeleteConfirmModal';
import { AddIcon, ArrowBackLong, Icon } from 'utils/icons';
import { useGlobalMutation } from 'utils/api/mutations';
import { useGlobalQuery } from 'utils/api/queries';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import { toastStyle } from 'utils/helper';

import './styles.scss';

const emptyForm = {
  name: '',
  abbreviation: '',
  description: '',
  calculation: '',
  units: '',
};

const formatGlobalValue = value => {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString(undefined, {
    maximumFractionDigits: 4,
  });
};

const getErrorMessage = error => {
  const data = error?.response?.data;
  if (!data) return error?.message || 'Something went wrong.';
  if (typeof data === 'string') return data;
  if (Array.isArray(data.usage) && data.usage.length) {
    const usagePreview = data.usage
      .slice(0, 5)
      .map(item => {
        const location = item.path || 'Unknown location';
        return item.field ? `${location} (${item.field})` : location;
      })
      .join('\n');
    const remaining =
      data.usage.length > 5 ? `\n...and ${data.usage.length - 5} more.` : '';
    return `${
      data.detail || 'This global is currently in use.'
    }\n${usagePreview}${remaining}`;
  }
  if (data.detail) return data.detail;
  if (data.non_field_errors?.[0]) return data.non_field_errors[0];
  const firstKey = Object.keys(data)[0];
  const firstValue = firstKey ? data[firstKey] : null;
  if (Array.isArray(firstValue)) return `${firstKey}: ${firstValue[0]}`;
  if (typeof firstValue === 'string') return `${firstKey}: ${firstValue}`;
  return 'Something went wrong.';
};

const GlobalsDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const queryParams = new URLSearchParams(location.search);
  const instanceType = queryParams.get('type') || 'budget';
  const isBudgetScope = instanceType === 'budget';
  const importSourceType = isBudgetScope ? 'user' : 'global';
  const { activeBudget, selectedBudget, hasEditAccess } =
    useHomeScreenContext();
  const canEdit = !isBudgetScope || hasEditAccess?.();

  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const currentFilters = useMemo(
    () => ({
      instance_type: instanceType,
      ...(isBudgetScope ? { active_id: activeBudget } : {}),
    }),
    [activeBudget, instanceType, isBudgetScope]
  );

  const suggestionFilters = useMemo(
    () => ({
      instance_type: importSourceType,
      ...(importSourceType === 'budget' ? { active_id: activeBudget } : {}),
    }),
    [activeBudget, importSourceType]
  );

  const {
    data: globalsResponse,
    isLoading,
    refetch: refetchGlobals,
  } = useGlobalQuery.useGlobalsQuery({
    filters: currentFilters,
    enabled: !isBudgetScope || Boolean(activeBudget),
  });

  const { data: suggestionsResponse, refetch: refetchSuggestions } =
    useGlobalQuery.useGlobalsQuery({
      filters: suggestionFilters,
      enabled: true,
    });

  const globals = useMemo(
    () => globalsResponse?.data?.data || globalsResponse?.data || [],
    [globalsResponse]
  );
  const suggestions = useMemo(
    () => suggestionsResponse?.data?.data || suggestionsResponse?.data || [],
    [suggestionsResponse]
  );

  const globalComparison = (a, b) =>
    a?.name === b?.name &&
    a?.abbreviation === b?.abbreviation &&
    a?.description === b?.description &&
    a?.calculation === b?.calculation &&
    a?.units === b?.units;

  const importSuggestions = suggestions.filter(
    item => !globals.some(global => globalComparison(global, item))
  );

  const invalidateGlobals = () => {
    queryClient.invalidateQueries({ queryKey: ['budget-globals'] });
    refetchGlobals();
    refetchSuggestions();
  };

  const createGlobalMutation = useGlobalMutation.useCreateGlobalMutation();
  const updateGlobalMutation = useGlobalMutation.useUpdateGlobalMutation();
  const deleteGlobalMutation = useGlobalMutation.useDeleteGlobalMutation();
  const importGlobalToUserMutation =
    useGlobalMutation.useImportGlobalToUserMutation();
  const importGlobalToBudgetMutation =
    useGlobalMutation.useImportGlobalToBudgetMutation();
  const isSavingGlobal =
    createGlobalMutation.isLoading || updateGlobalMutation.isLoading;

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleOpenCreateModal = () => {
    resetForm();
    setShowEditorModal(true);
  };

  const handleCloseEditorModal = () => {
    setShowEditorModal(false);
    resetForm();
  };

  const handleSubmit = event => {
    event?.preventDefault();
    if (!canEdit || isSavingGlobal) return;

    const payload = {
      ...form,
      instance_type: instanceType,
      ...(isBudgetScope ? { active_id: activeBudget } : {}),
    };

    const mutation = editingId ? updateGlobalMutation : createGlobalMutation;
    const requestPayload = editingId ? { id: editingId, ...payload } : payload;

    mutation.mutate(requestPayload, {
      onSuccess: () => {
        toast.success(editingId ? 'Global updated.' : 'Global added.');
        handleCloseEditorModal();
        invalidateGlobals();
      },
      onError: error => toast.error(getErrorMessage(error), toastStyle),
    });
  };

  const handleEdit = item => {
    setEditingId(item.id);
    setForm({
      name: item.name || '',
      abbreviation: item.abbreviation || '',
      description: item.description || '',
      calculation: item.calculation || '',
      units: item.units || '',
    });
    setShowEditorModal(true);
  };

  const handleDelete = item => {
    setDeleteTarget(item);
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget?.id) return;

    deleteGlobalMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success('Global deleted.');
        if (editingId === deleteTarget.id) resetForm();
        setDeleteTarget(null);
        invalidateGlobals();
        if (isBudgetScope) {
          [
            'get-budget-by-id',
            'get-budget-data',
            'budget-category-tree',
            'budget-categories',
            'get-swimlanes-in-budget',
            'get-swimlanes-details',
          ].forEach(key => queryClient.invalidateQueries({ queryKey: [key] }));
        }
      },
      onError: error => toast.error(getErrorMessage(error), toastStyle),
    });
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
  };

  const handleImport = item => {
    const mutation = isBudgetScope
      ? importGlobalToBudgetMutation
      : importGlobalToUserMutation;
    const payload = isBudgetScope
      ? { id: item.id, active_id: activeBudget }
      : item.id;

    mutation.mutate(payload, {
      onSuccess: () => {
        toast.success(`${item.abbreviation} imported.`);
        invalidateGlobals();
      },
      onError: error => toast.error(getErrorMessage(error), toastStyle),
    });
  };

  const pageTitle = isBudgetScope
    ? `${selectedBudget?.full_name || selectedBudget?.name || 'Budget'} Globals`
    : 'User Globals';

  return (
    <div className="globals-details-wrapper">
      <div className="globals-details-header">
        <div className="globals-details-heading">
          <button
            type="button"
            className="globals-back-button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ArrowBackLong />
          </button>
          <div>
            <h1>{pageTitle}</h1>
            <p>
              Define reusable values, then type an abbreviation like SD in a
              quantity, rate, or multiplier cell.
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary globals-add-button"
          onClick={handleOpenCreateModal}
          disabled={!canEdit}
        >
          <span>
            <Icon name="GlobeIcon" />
            Add New Global
          </span>
        </button>
      </div>

      <section className="globals-card">
        <div className="globals-table-wrapper">
          <table className="globals-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Abbreviation</th>
                <th>Description</th>
                <th>Calculation</th>
                <th>Units</th>
                <th>Value</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={7}>Loading globals...</td>
                </tr>
              )}
              {!isLoading &&
                globals.map(item => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td className="globals-abbreviation">
                      {item.abbreviation}
                    </td>
                    <td>{item.description}</td>
                    <td>{item.calculation}</td>
                    <td>{item.units}</td>
                    <td>{formatGlobalValue(item.value)}</td>
                    <td>
                      <div className="globals-row-actions">
                        <button
                          type="button"
                          onClick={() => handleEdit(item)}
                          aria-label={`Edit ${item.abbreviation}`}
                          disabled={!canEdit}
                        >
                          <Icon name="PenToSquareIcon" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item)}
                          aria-label={`Delete ${item.abbreviation}`}
                          disabled={!canEdit}
                        >
                          <Icon name="TrashCanIcon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              {!isLoading && globals.length === 0 && (
                <tr>
                  <td colSpan={7} className="globals-empty-state">
                    No globals have been added yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="globals-suggestions">
        <div className="globals-suggestions-title">
          {isBudgetScope ? 'Import from User Globals' : 'Import from Globals'}
        </div>
        <div className="globals-suggestions-items">
          {importSuggestions.length ? (
            importSuggestions.map(item => (
              <Chip
                key={item.id}
                className="globals-suggestion-chip"
                title={`${item.abbreviation} ${
                  item.name ? `- ${item.name}` : ''
                }`}
                icon={<AddIcon />}
                onClick={() => handleImport(item)}
                disabled={!canEdit}
              />
            ))
          ) : (
            <span className="globals-suggestions-empty">
              No globals available to import.
            </span>
          )}
        </div>
      </section>
      <DeleteConfirmModal
        showModal={Boolean(deleteTarget)}
        handleOnClose={handleDeleteCancel}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        confirmText="Are you sure you want to delete"
        itemName={deleteTarget?.abbreviation || 'this global'}
        description={
          isBudgetScope
            ? 'References to this global in the budget, including other globals’ formulas, will be replaced with its last calculated value. Current amounts will be kept, but this global will no longer update them.'
            : undefined
        }
        confirmLabel="Delete"
        disabled={deleteGlobalMutation.isLoading}
      />
      <Modal
        show={showEditorModal}
        onHide={handleCloseEditorModal}
        centered
        className="globals-editor-modal"
        dialogClassName="globals-editor-dialog"
      >
        <Modal.Body>
          <div className="globals-editor-modal-header">
            <div className="globals-editor-modal-title">
              <Icon name="GlobeIcon" className="globals-editor-modal-icon" />
              <h2>{editingId ? 'Edit Global' : 'Add New Global'}</h2>
            </div>
            <button
              type="button"
              className="globals-editor-close-button"
              onClick={handleCloseEditorModal}
              disabled={isSavingGlobal}
              aria-label="Close"
            >
              <Icon name="CloseIcon" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="globals-form">
            <div className="globals-form-grid">
              <label>
                <span>Name</span>
                <input
                  value={form.name}
                  onChange={event =>
                    setForm(prev => ({ ...prev, name: event.target.value }))
                  }
                  placeholder="Shooting Days"
                  disabled={!canEdit}
                />
              </label>
              <label>
                <span>Abbreviation</span>
                <input
                  value={form.abbreviation}
                  onChange={event =>
                    setForm(prev => ({
                      ...prev,
                      abbreviation: event.target.value.toUpperCase(),
                    }))
                  }
                  placeholder="SD"
                  maxLength={7}
                  disabled={!canEdit || Boolean(editingId)}
                  required
                />
              </label>
              <label>
                <span>Calculation</span>
                <input
                  value={form.calculation}
                  onChange={event =>
                    setForm(prev => ({
                      ...prev,
                      calculation: event.target.value,
                    }))
                  }
                  placeholder="10 or PD+TD"
                  disabled={!canEdit}
                  required
                />
              </label>
              <label>
                <span>Units</span>
                <input
                  value={form.units}
                  onChange={event =>
                    setForm(prev => ({ ...prev, units: event.target.value }))
                  }
                  placeholder="Days"
                  disabled={!canEdit}
                />
              </label>
            </div>
            <label className="globals-description-field">
              <span>Description</span>
              <textarea
                value={form.description}
                onChange={event =>
                  setForm(prev => ({
                    ...prev,
                    description: event.target.value,
                  }))
                }
                placeholder="Optional context for this global"
                disabled={!canEdit}
              />
            </label>
            <div className="globals-form-actions">
              <Button
                type="button"
                label="Cancel"
                className="globals-secondary-button"
                onClick={handleCloseEditorModal}
                isDisabled={isSavingGlobal}
              />
              <Button
                type="button"
                label={
                  isSavingGlobal
                    ? editingId
                      ? 'Updating...'
                      : 'Adding...'
                    : editingId
                    ? 'Update Global'
                    : 'Add Global'
                }
                className="globals-primary-button"
                onClick={handleSubmit}
                isDisabled={!canEdit || isSavingGlobal}
              />
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default GlobalsDetails;
